import { expVal } from '@atlassian/jira-feature-experiments';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	type Action,
	createStore,
	createContainer,
	createStateHook,
	createActionsHook,
	createHook,
} from '@atlassian/react-sweet-state';

export type RelatedIssue = {
	id: string;
	issueKey: IssueKey;
	issueLink: string;
	issueTypeIconUrl: string;
	issueSummary: string;
};

export type ResourceSuggestion = {
	resource: RelatedIssue;
	errorMessage?: unknown;
	resourceStatus: 'DRAFT' | 'ACCEPTED' | 'DISCARDED' | 'SELECTED';
};

export type Step = 'FETCHING' | 'LOADED' | 'DISPLAY';
export const steps = {
	fetching: 'FETCHING',
	display: 'DISPLAY',
	loaded: 'LOADED',
} as const;

export type InvokedFrom =
	| 'linkedWorkItemQuickAdd'
	| 'improveIssueDropdown'
	| 'issueLinksAddButton'
	| 'globalIssueCreate'
	| null;

// State of the Related issues context
type State = {
	currentStep: Step;
	resourceSuggestions?: ResourceSuggestion[];
	isLinkedIssuePanelButtonTriggered: boolean;
	errorMessage: unknown | null;
	displayLinkedIssuesPanelCallback?: (arg: boolean) => void;
	invokedFrom: InvokedFrom;
};

const initialState: State = {
	currentStep: steps.fetching,
	resourceSuggestions: undefined,
	isLinkedIssuePanelButtonTriggered: false,
	errorMessage: null,
	invokedFrom: null,
};

const setRelatedIssuesStep =
	(step: Step): Action<State> =>
	({ setState }) => {
		setState({ currentStep: step });
	};

const setInvokedFrom =
	(invokedFrom: InvokedFrom): Action<State> =>
	({ setState }) => {
		setState({ invokedFrom });
	};

const setRelatedIssuesFetchStep =
	(): Action<State> =>
	({ getState, setState }) => {
		const { displayLinkedIssuesPanelCallback } = getState();
		displayLinkedIssuesPanelCallback?.(true);
		setState({ currentStep: steps.fetching });
	};

const setRelatedIssuesDisplayStep =
	(): Action<State> =>
	({ setState }) => {
		setState({ currentStep: steps.display });
	};

const setRelatedIssuesLoadedStep =
	(): Action<State> =>
	({ setState }) => {
		setState({ currentStep: steps.loaded });
	};

const setIsLinkedIssuePanelButtonTriggered =
	(linkedIssuePanel: boolean): Action<State> =>
	({ setState }) => {
		setState({ isLinkedIssuePanelButtonTriggered: linkedIssuePanel });
	};

const setResourceSuggestion =
	(resources: RelatedIssue[]): Action<State> =>
	({ setState }) => {
		const resourceSuggestions: ResourceSuggestion[] = resources.map((resource) => ({
			resource,
			resourceStatus: expVal('jira-ai-similar-issues-selectable-draft-list', 'isEnabled', false)
				? 'SELECTED'
				: 'DRAFT',
		}));
		setState({ resourceSuggestions, currentStep: steps.loaded });
	};

const acceptSelectedSuggestions =
	(): Action<State> =>
	({ setState, getState }) => {
		const { resourceSuggestions } = getState();
		let allSelectedUpdated = true;
		const updatedSuggestions: ResourceSuggestion[] | undefined = resourceSuggestions?.map(
			(resourceSuggestion) => {
				if (resourceSuggestion.resourceStatus === 'SELECTED') {
					// No need to && true since it doesn't affect the value
					return {
						...resourceSuggestion,
						resourceStatus: 'ACCEPTED',
					};
				}
				allSelectedUpdated = false;
				return resourceSuggestion;
			},
		);

		setState({
			resourceSuggestions: updatedSuggestions,
			...(allSelectedUpdated && { currentStep: steps.loaded }),
		});
	};

const setResourceSuggestionStatus =
	(suggestion: RelatedIssue, status: 'DRAFT' | 'SELECTED'): Action<State> =>
	({ setState, getState }) => {
		const { resourceSuggestions } = getState();
		const updatedSuggestions: ResourceSuggestion[] | undefined = resourceSuggestions?.map(
			(resourceSuggestion) => {
				if (resourceSuggestion.resource.id === suggestion.id) {
					return {
						...resourceSuggestion,
						resourceStatus: status,
					};
				}
				return resourceSuggestion;
			},
		);
		setState({ resourceSuggestions: updatedSuggestions });
	};

const setErrorMessage =
	(message: unknown): Action<State> =>
	({ setState }) => {
		setState({ errorMessage: message, currentStep: steps.loaded });
	};

const acceptSuggestion =
	(suggestion: RelatedIssue): Action<State> =>
	({ setState, getState }) => {
		const { resourceSuggestions } = getState();
		const updatedSuggestions: ResourceSuggestion[] | undefined = resourceSuggestions?.map(
			(resourceSuggestion) => {
				if (resourceSuggestion.resource.id === suggestion.id) {
					return {
						...resourceSuggestion,
						resourceStatus: 'ACCEPTED',
					};
				}
				return resourceSuggestion;
			},
		);

		setState({
			resourceSuggestions: updatedSuggestions,
		});
	};

const acceptAllSuggestions =
	(): Action<State> =>
	({ setState, getState }) => {
		const { resourceSuggestions, displayLinkedIssuesPanelCallback } = getState();
		const updatedSuggestions: ResourceSuggestion[] | undefined = resourceSuggestions?.map(
			(resourceSuggestion) => {
				return {
					...resourceSuggestion,
					resourceStatus: 'ACCEPTED',
				};
			},
		);
		displayLinkedIssuesPanelCallback?.(false);
		setState({
			resourceSuggestions: updatedSuggestions,
			currentStep: steps.loaded,
		});
	};

const discardSuggestion =
	(suggestion: RelatedIssue): Action<State> =>
	({ setState, getState }) => {
		const { resourceSuggestions } = getState();
		const updatedSuggestions: ResourceSuggestion[] | undefined = resourceSuggestions?.map(
			(resourceSuggestion) => {
				if (resourceSuggestion.resource.id === suggestion.id) {
					return {
						...resourceSuggestion,
						resourceStatus: 'DISCARDED',
					};
				}
				return resourceSuggestion;
			},
		);
		setState({
			resourceSuggestions: updatedSuggestions,
		});
	};

const discardAllSuggestions =
	(): Action<State> =>
	({ getState, setState }) => {
		const { displayLinkedIssuesPanelCallback } = getState();
		displayLinkedIssuesPanelCallback?.(false);
		setState({
			currentStep: steps.loaded,
			resourceSuggestions: undefined,
		});
	};

const resetState =
	(): Action<State> =>
	({ getState, setState }) => {
		const { displayLinkedIssuesPanelCallback } = getState();
		displayLinkedIssuesPanelCallback?.(false);
		setState(initialState);
	};

const setDisplayLinkedIssuesPanelCallback =
	(cb: (arg: boolean) => void): Action<State> =>
	({ setState }) => {
		setState({ displayLinkedIssuesPanelCallback: cb });
	};

const actions = {
	acceptSuggestion,
	acceptAllSuggestions,
	discardSuggestion,
	discardAllSuggestions,
	setRelatedIssuesStep,
	setRelatedIssuesFetchStep,
	setRelatedIssuesLoadedStep,
	setIsLinkedIssuePanelButtonTriggered,
	resetState,
	setResourceSuggestion,
	setInvokedFrom,
	setErrorMessage,
	setRelatedIssuesDisplayStep,
	setDisplayLinkedIssuesPanelCallback,
	setResourceSuggestionStatus,
	acceptSelectedSuggestions,
};
type ActionsType = typeof actions;

export const JiraIssueRelatedIssuesContainer = createContainer();
export const Store = createStore<State, ActionsType>({
	initialState,
	actions,
	name: 'jira-ai-related-issues',
	containedBy: JiraIssueRelatedIssuesContainer,
});

/**
 * Specific container for mocking the state in storybook
 */
export const StorybookContainer = createContainer<State, ActionsType, State>(Store, {
	onInit:
		() =>
		({ setState }, mockedState) =>
			setState(mockedState),
});

export const useRelatedIssues = createHook(Store);

export const useRelatedIssuesState = createStateHook(Store);

export const useRelatedIssuesActions = createActionsHook(Store);
